import { useEffect, useState, useCallback } from "react";
import api, { billingErrorMessage } from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TYPES = [
  "blog_post", "buying_guide", "comparison_page", "landing_page",
  "collection_page", "case_study", "ai_search_answer_page", "product_page",
];

export default function ArticleGenerator() {
  const { activeProjectId } = useProjects();
  const [articles, setArticles] = useState([]);
  const [contentType, setContentType] = useState("blog_post");
  const [keyword, setKeyword] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [step, setStep] = useState("idle"); // idle | brief | outline | draft

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    const { data } = await api.get(`/articles/${activeProjectId}`);
    setArticles(data);
  }, [activeProjectId]);
  useEffect(() => { load(); }, [load]);

  const startBrief = async () => {
    if (!activeProjectId) return;
    setLoading(true);
    setStep("brief");
    try {
      const { data } = await api.post("/articles/start", {
        project_id: activeProjectId,
        content_type: contentType,
        target_keyword: keyword,
        target_url: targetUrl,
      });
      setActive({ id: data.article_id, brief: data.brief, outline: null, draft: null });
      toast.success("Brief generated");
    } catch (e) {
      toast.error(billingErrorMessage(e) || "Brief failed");
    } finally {
      setLoading(false);
    }
  };

  const genOutline = async () => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/articles/${active.id}/outline`);
      setActive((a) => ({ ...a, outline: data.outline }));
      setStep("outline");
    } catch (e) {
      toast.error(billingErrorMessage(e) || "Outline failed");
    } finally {
      setLoading(false);
    }
  };

  const genDraft = async () => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/articles/${active.id}/draft`);
      setActive((a) => ({ ...a, draft: data.draft }));
      setStep("draft");
      toast.success("Draft ready");
      load();
    } catch (e) {
      toast.error(billingErrorMessage(e) || "Draft failed");
    } finally {
      setLoading(false);
    }
  };

  const openExisting = async (a) => {
    const { data } = await api.get(`/articles/detail/${a.id}`);
    setActive({ id: data.id, brief: data.brief, outline: data.outline, draft: data.draft });
    setStep(data.draft ? "draft" : data.outline ? "outline" : "brief");
  };

  return (
    <DashboardLayout>
      <PageHeader
        overline="AI Article Generator"
        title="Brief → outline → publish-ready draft."
        description="Every article ships with meta, FAQ schema, internal links, and CTA blocks — automatically tuned to your business model."
      />

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-5">
          <div className="bg-white border border-border rounded-sm p-5">
            <p className="editorial-overline">Create new</p>
            <div className="mt-4 space-y-3">
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger className="rounded-sm bg-white" data-testid="article-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Target keyword" data-testid="article-keyword" className="bg-white border-border rounded-sm" />
              <Input value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} placeholder="Target URL hint" data-testid="article-url" className="bg-white border-border rounded-sm" />
              <button onClick={startBrief} disabled={loading} data-testid="generate-brief-btn" className="w-full bg-navy-900 hover:bg-navy-800 text-ivory-50 font-semibold rounded-sm py-2.5 text-sm transition-colors disabled:opacity-50">
                {loading ? "Generating…" : "Generate Article Brief"}
              </button>
            </div>
          </div>

          <div className="bg-white border border-border rounded-sm p-5">
            <p className="editorial-overline">Recent</p>
            <ul className="mt-3 divide-y divide-border">
              {articles.length === 0 && <li className="text-sm text-muted-foreground py-3">No articles yet.</li>}
              {articles.map((a) => (
                <li key={a.id} className="py-3">
                  <button onClick={() => openExisting(a)} className="text-left w-full hover:text-gold-600" data-testid={`article-row-${a.id}`}>
                    <p className="text-sm font-medium">{a.target_keyword}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.content_type.replace(/_/g, " ")} · {a.status}</p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-5">
          {!active && (
            <div className="border-2 border-dashed border-border rounded-sm p-12 text-center bg-white">
              <p className="font-serif text-xl text-muted-foreground">Generate a brief to begin.</p>
            </div>
          )}

          {active?.brief && (
            <div className="bg-white border border-border rounded-sm p-6" data-testid="article-brief-panel">
              <p className="editorial-overline">Brief</p>
              <h3 className="font-serif text-2xl mt-1">{active.brief.angle}</h3>
              <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
                <Field label="Search intent" value={active.brief.search_intent} />
                <Field label="Target word count" value={`${active.brief.target_word_count} words`} />
                <Field label="Primary keyword" value={active.brief.primary_keyword} />
                <Field label="Conversion hypothesis" value={active.brief.conversion_hypothesis} />
              </div>
              <div className="mt-5">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Secondary keywords</p>
                <div className="flex flex-wrap gap-2">
                  {(active.brief.secondary_keywords || []).map((k) => (
                    <span key={k} className="px-2.5 py-1 text-xs bg-ivory-100 border border-border rounded-sm">{k}</span>
                  ))}
                </div>
              </div>
              {!active.outline && (
                <button onClick={genOutline} disabled={loading} data-testid="generate-outline-btn" className="mt-6 px-5 py-2.5 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm">
                  {loading ? "Generating…" : "Generate Outline"}
                </button>
              )}
            </div>
          )}

          {active?.outline && (
            <div className="bg-white border border-border rounded-sm p-6" data-testid="article-outline-panel">
              <p className="editorial-overline">Outline</p>
              <ol className="mt-3 space-y-3">
                {active.outline.map((s) => (
                  <li key={s.heading} className="border-l-2 border-gold-500 pl-4">
                    <p className="font-serif text-lg">{s.heading}</p>
                    <p className="text-sm text-muted-foreground mt-1">{s.summary}</p>
                  </li>
                ))}
              </ol>
              {!active.draft && (
                <button onClick={genDraft} disabled={loading} data-testid="generate-draft-btn" className="mt-6 px-5 py-2.5 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm">
                  {loading ? "Generating…" : "Generate Article Draft"}
                </button>
              )}
            </div>
          )}

          {active?.draft && (
            <div className="bg-white border border-border rounded-sm p-6" data-testid="article-draft-panel">
              <p className="editorial-overline">Draft</p>
              <h3 className="font-serif text-2xl mt-1">{active.draft.meta_title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{active.draft.meta_description}</p>
              <div className="mt-3 text-xs text-muted-foreground">/{active.draft.slug}</div>

              <div className="mt-5 markdown-body">
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-navy-900">{active.draft.markdown}</pre>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="editorial-overline">FAQ schema</p>
                  <ul className="mt-2 space-y-2 text-sm">
                    {(active.draft.faq || []).map((f) => (
                      <li key={f.q}><span className="font-medium">{f.q}</span><br /><span className="text-muted-foreground">{f.a}</span></li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="editorial-overline">Internal links</p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {(active.draft.internal_links || []).map((l) => (
                      <li key={l.url}><span className="font-medium">{l.anchor}</span> → <code className="text-xs">{l.url}</code></li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 border border-gold-500/40 bg-gold-500/5 p-4 rounded-sm">
                <p className="editorial-overline">CTA block</p>
                <p className="font-serif text-lg mt-1">{active.draft.cta_block}</p>
              </div>

              <div className="mt-6 flex gap-3">
                <button onClick={() => toast.success("Marked ready to publish")} className="px-5 py-2.5 bg-navy-900 hover:bg-navy-800 text-ivory-50 font-semibold rounded-sm text-sm" data-testid="article-mark-ready">
                  Mark ready to publish
                </button>
                <button onClick={() => toast.message("CMS publish placeholder — wire your CMS in /integrations.")} className="px-5 py-2.5 border border-border hover:border-gold-500 rounded-sm text-sm" data-testid="article-cms-publish">
                  Publish to CMS (placeholder)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-sm text-navy-900 mt-1">{value}</p>
    </div>
  );
}
